/**
 * Meta CAPI `_fbc` (Facebook Click ID) and `_fbp` (Facebook Browser ID) cookies.
 *
 * `_fbc` format: `fb.<subdomainIndex>.<creationTime>.<fbclid>`
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */
import { getCookies } from './cookie'

export const FBC_COOKIE = '_fbc'
export const FBP_COOKIE = '_fbp'

/** 90 days — the lifetime Meta uses for `_fbc`. */
const FBC_MAX_AGE = 90 * 24 * 60 * 60

/** The click id itself may contain dots, so it is the whole remainder of the value. */
const FBC_PATTERN = /^fb\.(\d+)\.(\d+)\.(.+)$/

export type ParsedFbc = {
  subdomainIndex: number
  creationTime: number
  fbclid: string
}

export function parseFbc(value: string): ParsedFbc | null {
  const match = FBC_PATTERN.exec(value)

  if (!match) return null

  return {
    subdomainIndex: Number(match[1]),
    creationTime: Number(match[2]),
    fbclid: match[3],
  }
}

/**
 * The `_fbc` value to report to CAPI, verbatim — Meta matches on the exact string.
 *
 * The cookie name can be present more than once (ours next to the one the Meta Pixel writes
 * from GTM), so the most recent well-formed value wins rather than whichever the browser
 * happens to list first.
 */
export function readFbc(): string {
  const values = getCookies(FBC_COOKIE)

  let latest = ''
  let latestTime = -1

  for (const value of values) {
    const parsed = parseFbc(value)

    if (!parsed || parsed.creationTime <= latestTime) continue

    latest = value
    latestTime = parsed.creationTime
  }

  // Nothing matched the documented format — pass the cookie through unchanged rather than
  // reporting no click id at all.
  return latest || values[0] || ''
}

export function readFbp(): string {
  return getCookies(FBP_COOKIE)[0] ?? ''
}

function getFbclidFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('fbclid')
}

/**
 * Stores the `fbclid` from the current URL as `_fbc`.
 *
 * Runs at bootstrap and again on every location change, so a click id that arrives on an
 * in-app navigation is not missed. An already stored cookie for the same click id is kept,
 * which preserves its original creation time.
 */
export function syncFbcWithUrl(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const fbclid = getFbclidFromUrl()

  if (!fbclid) return
  if (parseFbc(readFbc())?.fbclid === fbclid) return

  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  document.cookie = `${FBC_COOKIE}=fb.1.${Date.now()}.${fbclid}; path=/; max-age=${FBC_MAX_AGE}; SameSite=Lax${secure}`
}
